import { motion } from "framer-motion";

import type { BlogPost } from "@/lib/api";
import { cn } from "@/lib/formatters";

interface BlogTableProps {
  posts: BlogPost[];
}

function posColor(pos: number): string {
  if (pos <= 10) return "text-success";
  if (pos <= 20) return "text-primary";
  if (pos <= 30) return "text-warn";
  return "text-muted";
}

function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

const CATEGORY_HUE: Record<string, string> = {
  ai:         "text-primary border-primary/40 bg-primary/5",
  automation: "text-violet border-violet/40 bg-violet/5",
  webdev:     "text-magenta border-magenta/40 bg-magenta/5",
  seo:        "text-success border-success/40 bg-success/5",
  digital:    "text-warn border-warn/40 bg-warn/5",
};

export function BlogTable({ posts }: BlogTableProps) {
  if (posts.length === 0) {
    return (
      <div className="surface p-12 text-center">
        <p className="font-mono text-sm text-muted">$ no posts match the filters</p>
      </div>
    );
  }

  return (
    <div className="surface overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-elev/30 border-b border-border">
            <tr className="text-left text-xs font-mono uppercase tracking-wider text-muted">
              <th className="px-4 py-3">post</th>
              <th className="px-2 py-3">cat</th>
              <th className="px-2 py-3">published</th>
              <th className="px-2 py-3 text-right">clk</th>
              <th className="px-2 py-3 text-right">imp</th>
              <th className="px-2 py-3 text-right">ctr</th>
              <th className="px-2 py-3 text-right">pos</th>
              <th className="px-2 py-3 text-right">link</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p, i) => {
              const catClass = CATEGORY_HUE[p.category] ?? "text-muted border-border bg-bg-elev/40";
              return (
                <motion.tr
                  key={p.slug}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.015, 0.3), duration: 0.25 }}
                  className="border-b border-border/40 last:border-0 hover:bg-violet/[0.04] transition-colors"
                >
                  <td className="px-4 py-3 max-w-[420px]">
                    <p className="text-text truncate" title={p.title_es}>{p.title_es || p.slug}</p>
                    <p className="text-xs text-muted font-mono truncate mt-0.5" title={p.keyword}>
                      {p.keyword || "—"}
                    </p>
                  </td>
                  <td className="px-2 py-3">
                    <span className={cn(
                      "inline-block px-1.5 py-0.5 rounded-sm font-mono text-[10px] uppercase border",
                      catClass,
                    )}>
                      {p.category || "—"}
                    </span>
                  </td>
                  <td className="px-2 py-3 font-mono text-xs text-muted whitespace-nowrap">
                    {fmtDate(p.published_at)}
                  </td>
                  {p.perf ? (
                    <>
                      <td className="px-2 py-3 text-right font-mono text-success">{p.perf.clicks}</td>
                      <td className="px-2 py-3 text-right font-mono text-primary">{p.perf.impressions}</td>
                      <td className="px-2 py-3 text-right font-mono text-muted">{fmtPct(p.perf.ctr)}</td>
                      <td className={cn("px-2 py-3 text-right font-mono", posColor(p.perf.position))}>
                        {p.perf.position.toFixed(1)}
                      </td>
                    </>
                  ) : (
                    <td colSpan={4} className="px-2 py-3 text-right font-mono text-xs text-muted/50">
                      no gsc data
                    </td>
                  )}
                  <td className="px-2 py-3 text-right">
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-primary hover:text-magenta transition-colors font-mono text-xs"
                      title={p.url}
                    >
                      ↗
                    </a>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
